/**
 * Contratos padrão do sistema, em forma de DADOS.
 *
 * O documento de aceite (ModalTerms) era JSX cravado no componente. Virou
 * estrutura porque agora existem três consumidores do mesmo conteúdo:
 *
 *  · o modal de aceite e o "Ver contrato" do menu, que renderizam;
 *  · o editor do representante (CompanyContractEditor), que precisa carregar o
 *    padrão como ponto de partida para alterar cláusula por cláusula;
 *  · o sumário lateral do modal, que antes era uma segunda lista mantida à mão
 *    e saía de sincronia com o documento ao primeiro texto editado.
 *
 * O que NÃO está aqui: a numeração e os ids das cláusulas. Ambos são derivados
 * da posição (`clausula-1`, `1ª`) em buildContractSections/ContractDocument —
 * guardá-los permitiria salvar um contrato com duas "3ª" ou com uma âncora que
 * o sumário não encontra.
 *
 * `shortTitle` existe só para o sumário: os contratos padrão sempre tiveram no
 * índice um rótulo mais curto que o título da cláusula no corpo do documento.
 * Ausente, o sumário usa o próprio `title` — que é o caso de todo contrato
 * montado no editor.
 */

/** Modalidade pós-paga (faturamento mensal). */
export const DEFAULT_POST_PAID_CONTRACT = {
  title: "Contrato de Prestação de Serviços — Termos de Uso",
  subtitle: "",
  intro:
    "Pelo presente instrumento particular, CONTRATANTE e CONTRATADA, têm pactuado entre si o presente contrato, mediante as cláusulas e condições a seguir expostas:",
  clauses: [
    {
      title: "Do objeto",
      items: [
        "O objeto do Contrato é a prestação de serviços de disponibilização pela CONTRATADA à CONTRATANTE de bancos de dados, informações de cadastros relativos aos consumidores (pessoas físicas ou jurídicas) em âmbito nacional, bem como serviço de proteção ao crédito e congêneres.",
        "A CONTRATANTE, através do meio de acesso retro descrito, realizará consultas às informações cadastrais disponíveis nos Bancos de Dados, acessadas diretamente ou através da CONTRATADA.",
      ],
    },
    {
      title: "Da utilização do software e obrigações dos contratantes",
      shortTitle: "Da utilização do software e obrigações",
      items: [
        "A CONTRATANTE passará a fazer parte do quadro de CLIENTES da CONTRATADA a partir da assinatura deste contrato, feita presencialmente e/ou eletronicamente, pressupondo leitura e aceite de todas as cláusulas.",
        "As consultas serão feitas no site da CONTRATADA com login e senha fornecidos.",
        "Consultas de CNPJ ou CPF, conforme o tipo da pessoa, e para cheques e veículos, deverá informar os dados necessários.",
        "A concessão de crédito ou decisões com base nas informações é de inteira responsabilidade da CONTRATANTE.",
        "A CONTRATANTE é responsável pela guarda e sigilo de suas senhas e deve comunicar qualquer uso indevido.",
        "O contrato permanece válido mesmo com mudanças administrativas na CONTRATANTE.",
        "A CONTRATADA pode alterar sites e fontes de dados sem aviso prévio.",
        "O uso de novos produtos ou funcionalidades implica adesão automática no primeiro uso.",
        "É responsabilidade da CONTRATANTE acompanhar as atualizações no site da CONTRATADA.",
        "É vedada a divulgação, revenda ou uso indevido das informações obtidas.",
        "As informações devem ser usadas exclusivamente para análise de crédito e negócios.",
        "A CONTRATADA não se responsabiliza por perdas ou danos causados pela interpretação das informações.",
      ],
    },
    {
      title: "Dos valores, reajuste e forma de pagamento",
      shortTitle: "Dos valores, reajuste e pagamento",
      items: [
        "Faturamento mensal via fatura enviada para o e-mail da CONTRATANTE, conforme ANEXO I.",
        "Se o boleto não for recebido, a CONTRATANTE deve solicitá-lo à CONTRATADA.",
        "Pagamentos em atraso terão juros de 1% ao mês, correção pelo IGP-M e multa de 2%.",
        "Dados da CONTRATANTE poderão ser incluídos em cadastros de inadimplência.",
        "O sistema será bloqueado após 5 dias de atraso e reativado após regularização.",
        "O contrato continua gerando cobranças mesmo com o sistema bloqueado.",
        "A CONTRATADA pode rescindir o contrato em caso de inadimplência.",
      ],
    },
    {
      title: "Do reajuste",
      items: [
        "Reajuste anual em junho com base no IGP-M ou outros índices permitidos por lei.",
        "A CONTRATANTE é responsável por todos os valores gerados por seu uso do sistema.",
      ],
    },
    {
      title: "Do sigilo, confidencialidade e segurança das informações",
      shortTitle: "Do sigilo, confidencialidade e segurança",
      items: [
        "Login e senha são pessoais, intransferíveis e considerados assinatura eletrônica.",
        "A CONTRATANTE deve manter o sigilo de seus acessos e proteger seu ambiente de rede.",
        "A CONTRATANTE confirma o recebimento da senha e treinamento no ato da contratação.",
        "É obrigatória a adoção de práticas de segurança como antivírus, firewall e antispyware.",
        "As partes se comprometem a cumprir a Lei 13.709/18 (LGPD).",
      ],
    },
    {
      title: "Da vigência e rescisão",
      items: [
        "Contrato com vigência mínima de 12 meses e renovação automática.",
        "Para não renovação, é necessário aviso prévio de 30 dias antes do fim da vigência.",
        "Rescisão antecipada implica multa de 10% da franquia vezes os meses restantes.",
        "Não poderá haver pendências no momento da solicitação de rescisão.",
        "Consultas realizadas no mês da rescisão serão cobradas integralmente.",
        "A CONTRATANTE é responsável por excluir protestos registrados por ela.",
      ],
    },
    {
      title: "Das condições gerais e fiança",
      items: [
        "O signatário do contrato se torna fiador e responsável pelos compromissos assumidos.",
        "O fiador renuncia a benefícios legais de ordem e pode ter seus dados negativados.",
      ],
    },
    {
      title: "Do foro",
      text: "Fica eleito o Foro da Comarca de São Paulo - SP para resolver quaisquer controvérsias.",
    },
  ],
  closing:
    "E por estarem assim justos e contratados, conhecedores de todas as cláusulas do presente contrato, aceitando as condições estipuladas, assinam o presente contrato, sem quaisquer restrições ou dúvidas, em duas vias na presença de duas testemunhas.",
  place: "São Paulo",
};

/** Modalidade pré-paga (consumo de saldo). */
export const DEFAULT_PRE_PAID_CONTRACT = {
  title: "Contrato de Prestação de Serviços — Termos de Uso",
  subtitle: "Modalidade pré-paga",
  intro:
    "Pelo presente instrumento, a empresa responsável pela plataforma, doravante denominada CONTRATADA, e a pessoa física ou jurídica identificada pelos dados cadastrais registrados eletronicamente na plataforma, doravante denominada CONTRATANTE, celebram o presente Contrato de Prestação de Serviços, mediante as condições abaixo.",
  clauses: [
    {
      title: "Do objeto",
      items: [
        "A CONTRATADA disponibilizará à CONTRATANTE acesso à plataforma para realização de consultas cadastrais, comerciais, financeiras, creditícias e demais produtos disponibilizados no sistema, referentes a pessoas físicas ou jurídicas.",
        "As informações poderão ser provenientes de bancos de dados próprios ou de terceiros e estarão sujeitas à disponibilidade e atualização das respectivas fontes.",
      ],
    },
    {
      title: "Do acesso e das obrigações da contratante",
      items: [
        "O acesso será realizado por login e senha de uso pessoal, sendo a CONTRATANTE responsável pela guarda e pelo uso de suas credenciais.",
        "A CONTRATANTE compromete-se a utilizar as informações exclusivamente para finalidades legítimas relacionadas às suas atividades, sendo vedado uso ilícito, divulgação, comercialização ou compartilhamento não autorizado.",
        "As decisões tomadas com base nas informações consultadas, inclusive concessão ou negativa de crédito, são de exclusiva responsabilidade da CONTRATANTE. A CONTRATADA não garante aprovação de crédito nem resultado comercial específico.",
      ],
    },
    {
      title: "Da modalidade pré-paga e dos créditos",
      items: [
        "Os serviços serão prestados na modalidade PRÉ-PAGA. A CONTRATANTE deverá adquirir saldo antecipadamente, e cada consulta ou serviço realizado será automaticamente debitado do saldo disponível, conforme o preço vigente apresentado na plataforma.",
        "Sem saldo suficiente, novas consultas ficarão indisponíveis até nova recarga. A ausência de saldo não gera dívida, juros, multa ou cobrança mensal.",
        "Os preços poderão ser alterados pela CONTRATADA, valendo para cada operação o valor informado na plataforma no momento da utilização.",
        "Em caso comprovado de débito decorrente de falha da plataforma sem a efetiva entrega do serviço, o respectivo valor será restituído ao saldo da CONTRATANTE.",
      ],
    },
    {
      title: "Da proteção de dados e do uso legítimo",
      shortTitle: "Da proteção de dados e uso legítimo",
      items: [
        "As partes comprometem-se a observar a Lei nº 13.709/2018 — Lei Geral de Proteção de Dados Pessoais (LGPD) e demais normas aplicáveis.",
        "A CONTRATANTE declara possuir finalidade legítima para as consultas realizadas e assume responsabilidade pelo tratamento e utilização das informações obtidas.",
        "É proibida a realização de consultas para finalidade ilícita, mera curiosidade, perseguição, divulgação ou comercialização não autorizada de dados.",
        "A CONTRATADA poderá manter registros de acessos e consultas para fins de segurança, auditoria, prevenção a fraudes, cumprimento de obrigações legais e defesa de direitos.",
      ],
    },
    {
      title: "Da suspensão e da limitação de responsabilidade",
      items: [
        "A CONTRATADA poderá suspender ou cancelar o acesso em caso de uso irregular ou ilícito da plataforma, fraude ou suspeita fundamentada de fraude, utilização indevida de dados, compartilhamento não autorizado de credenciais ou violação destes Termos.",
        "A CONTRATADA não responderá por indisponibilidades ou divergências decorrentes de bancos de dados ou fornecedores terceiros, ressalvadas as responsabilidades que legalmente lhe sejam atribuídas.",
      ],
    },
    {
      title: "Da vigência e do encerramento",
      items: [
        "Este contrato entra em vigor com o aceite eletrônico e permanecerá válido enquanto a conta estiver ativa.",
        "Por se tratar de modalidade pré-paga, não há consumo mínimo mensal, renovação obrigatória ou multa pelo simples encerramento da utilização, salvo condição comercial específica expressamente contratada.",
        "A CONTRATANTE poderá deixar de utilizar a plataforma a qualquer momento.",
      ],
    },
    {
      title: "Das disposições gerais e do foro",
      shortTitle: "Das disposições gerais e foro",
      items: [
        "O cadastro, o aceite eletrônico, os registros de acesso, recargas e utilização da plataforma poderão ser utilizados como comprovação da relação contratual e das operações realizadas.",
        "A CONTRATADA poderá atualizar estes Termos para adequações legais, regulatórias ou operacionais, disponibilizando a versão vigente por meio eletrônico.",
        "Fica eleito o Foro da Comarca de São Paulo - SP, observadas as regras legais de competência, para dirimir eventuais controvérsias decorrentes deste contrato.",
      ],
    },
  ],
  closing:
    "A CONTRATANTE declara ter lido e compreendido integralmente as cláusulas acima, manifestando sua concordância por meio do aceite eletrônico registrado na plataforma.",
  place: "São Paulo",
};

/* As duas modalidades do sistema. O valor é o mesmo gravado em
   Company.typePayment, e é a chave do contrato no banco. */
export const CONTRACT_TYPES = [
  { value: "PRE", label: "Cliente pré-pago" },
  { value: "POS", label: "Cliente pós-pago" },
];

export const isPrePaidTerms = (typePayment) => typePayment === "PRE";

/* Qualquer coisa que não seja "PRE" cai no pós-pago, que era o comportamento
   do modal antes de existir contrato configurável (o campo pode vir nulo em
   cadastro antigo). */
export const normalizeContractType = (typePayment) =>
  isPrePaidTerms(typePayment) ? "PRE" : "POS";

export const getDefaultContract = (typePayment) =>
  isPrePaidTerms(typePayment)
    ? DEFAULT_PRE_PAID_CONTRACT
    : DEFAULT_POST_PAID_CONTRACT;

/**
 * Marcação aceita no texto de um contrato montado no editor.
 *
 * Lista fechada e sem NENHUM atributo: o texto é digitado por um representante
 * e renderizado no navegador do cliente dele, então tudo que carrega atributo
 * (href, src, style, on*) fica fora — é o que apaga a superfície de XSS em vez
 * de tentar filtrá-la. Um contrato não precisa de link nem de imagem, e a
 * barra do editor não oferece os dois.
 *
 * O backend repete esta lista em companyContractService (os dois lados
 * precisam concordar; o servidor recusa o que estiver fora, e o DOMPurify no
 * RichTextViewer é a proteção de verdade na renderização).
 */
export const CONTRACT_RICH_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "ul",
  "ol",
  "li",
];

const escapeHtml = (texto) =>
  String(texto || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Corpo de uma cláusula como HTML, para abrir no editor de texto rico.
 *
 * Os contratos padrão (e as linhas gravadas antes do editor virar rich text)
 * guardam `items` ou `text` em texto puro; o editor trabalha só com HTML. A
 * conversão mora aqui porque é o mesmo caminho para os dois formatos antigos, e
 * escapa o conteúdo: texto puro que tenha um "<" não pode virar tag ao ser
 * aberto para edição.
 */
export const clauseBodyToHtml = (clause) => {
  if (clause?.html) return clause.html;

  const items = Array.isArray(clause?.items) ? clause.items : [];
  if (items.length) {
    return `<ul>${items
      .map((item) => `<li>${escapeHtml(item)}</li>`)
      .join("")}</ul>`;
  }

  return clause?.text ? `<p>${escapeHtml(clause.text)}</p>` : "";
};

/** Mesma conversão para os parágrafos soltos (abertura e encerramento). */
export const paragraphToHtml = (texto) =>
  texto ? `<p>${escapeHtml(texto)}</p>` : "";

/* Quill devolve isto quando o campo está vazio — não é conteúdo. */
export const isEmptyRichText = (html) =>
  !String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

/**
 * Sumário lateral do modal de aceite, derivado do próprio documento.
 *
 * Os ids precisam casar com as âncoras que o ContractDocument escreve — por
 * isso os dois derivam da posição, e não de nada guardado no contrato.
 */
export const buildContractSections = (contract) =>
  (contract?.clauses || []).map((clause, index) => ({
    id: `clausula-${index + 1}`,
    number: `${index + 1}ª`,
    title: clause?.shortTitle || clause?.title || `Cláusula ${index + 1}`,
  }));
